/**
 * AqSection — the adequacy questionnaire, inline on the MiFID tab (#52).
 *
 * Pattern A throughout: answers stage in page-level state (`draft`),
 * version deletes stage in `pendingDeletes`, the allocation pick stages
 * into the page form — the page's Save Changes commits everything.
 *
 * Polish round 2 (Pedro 2026-06-12, QUESTIONNAIRE_SPEC.md §9):
 * - The correspondence table's allocations are coloured PILLS (chart
 *   palette) and act as the Strategic Allocation picker: click stages,
 *   re-click freely, Save fixes. Picking ABOVE the achieved score's row
 *   flashes a warning (conservative picks stay quiet); the persistent
 *   under-field warning + the Compliance flag are server-truths.
 * - The history is a working object: row click opens that version
 *   read-only; per-row pencil loads THAT version into the form (Save
 *   files a NEW version — history append-only); per-row delete STAGES
 *   (commits on Save); both enabled only in Edit mode.
 * - Columns ordered like the fields above: Suitability score | ESG |
 *   Allocation chosen | Date | Advisory notes | actions.
 */
import {
  Alert, Badge, Button, Card, Checkbox, Divider, FileButton, Grid, Group, Modal,
  NumberInput, Radio, Stack, Table, Text, Textarea, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconArrowBackUp, IconCalculator, IconClipboardCheck,
  IconEraser, IconPencil, IconTrash, IconUpload, IconX,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

import { allocationExceeds, matchConversionRow } from '../api/aq';
import { importAqPdf } from '../api/aqImports';
import type {
  AqPreview, AqResponse, EsgScore, StrategicAllocation, SuitabilityConversionRow, UUID,
} from '../api/types';
import { useAqPreview, useAqResponses, useSuitabilityConversion } from '../hooks/useAq';
import { useAlertModal } from './AlertModalProvider';
import { SaaAnalysisModal } from './SaaAnalysisModal';
import type { SaaSection } from './SaaAnalyticsView';
import { SaaMatrixView } from './SaaMatrixView';
import { allocLabel } from '../utils/allocation';
import { Q8_OPTIONS } from '../utils/q8Bands';

// ─── Draft shape (page-level state) ────────────────────────────────────────

export interface AqDraft {
  answers: Record<string, unknown>;
  financials: Record<string, unknown>;
  notes: string;
}

export const EMPTY_AQ_DRAFT: AqDraft = { answers: {}, financials: {}, notes: '' };

// ─── Question config (verbatim option texts, canonical keys) ──────────────

type Opt = { value: string; label: string };
const Q: Record<string, { label: string; options: Opt[] }> = {
  q1_purpose: { label: 'Investment Purpose (select all that apply)', options: [
    { value: 'capital_preservation', label: 'Capital preservation' },
    { value: 'regular_liquidity', label: 'Regular liquidity generation' },
    { value: 'long_term_appreciation', label: 'Long term capital appreciation' },
    { value: 'financing_long_term', label: 'Financing long term expenses' },
    { value: 'other', label: 'Other' },
  ]},
  q2_horizon: { label: 'Time horizon', options: [
    { value: 'lt_1y', label: 'less than 1 year' },
    { value: 'y1_3', label: '1-3 years' },
    { value: 'y3_5', label: '3-5 years' },
    { value: 'y5_10', label: '5-10 years' },
    { value: 'y10_15', label: '10-15 years' },
    { value: 'y15_plus', label: '15+ years' },
  ]},
  q4_asset_change: { label: 'Expected Change in Total Available Financial Assets', options: [
    { value: 'increase', label: 'Significant Increase (>20%)' },
    { value: 'no_change', label: 'No significant change' },
    { value: 'decrease', label: 'Significant decrease (<-20%)' },
  ]},
  q5_income_change: { label: 'Expected Change in Annual Income (net)', options: [
    { value: 'increase', label: 'Significant Increase (>20%)' },
    { value: 'no_change', label: 'No significant change' },
    { value: 'decrease', label: 'Significant decrease (<-20%)' },
  ]},
  q7_risk_return: { label: 'What is your preferred long term risk/return profile?', options: [
    { value: 'stable_low', label: 'Stable assets; low expected returns' },
    { value: 'moderate_average', label: 'Moderate volatility; average expected returns' },
    { value: 'high_high', label: 'High volatility; High expected returns' },
  ]},
  q8_devaluation: { label: 'Assuming the expected return over the medium term compensates for it, what is your tolerance to temporary devaluations over 1 year (estimates with 99% probability)?', options: Q8_OPTIONS },
  q10_experience: { label: 'How many years of experience do you have in investing in higher volatility assets, such as stocks or derivatives, either directly or via funds?', options: [
    { value: 'none', label: 'None' },
    { value: 'lt_3y', label: 'Less than 3 years' },
    { value: 'y3_6', label: '3-6 years' },
    { value: 'y6_15', label: '6-15 years' },
    { value: 'before_2009_crisis', label: 'Since before the 2007-2009 financial crisis' },
    { value: 'before_2002_crisis', label: 'Since before the 2000-2002 financial crisis' },
  ]},
  q11_last_trade: { label: 'When was the last time that you executed trades on your financial asset portfolio?', options: [
    { value: 'never', label: 'Never' },
    { value: 'gt_5y', label: 'More than 5 years ago' },
    { value: 'y1_5', label: '1-5 years ago' },
    { value: 'lt_1y', label: 'Less than 1 year ago' },
    { value: 'regularly', label: 'I trade regularly' },
  ]},
  q12_news: { label: 'How frequently do you follow the news on financial markets?', options: [
    { value: 'never', label: 'Never' },
    { value: 'rarely', label: 'Rarely' },
    { value: 'sometimes', label: 'Sometimes' },
    { value: 'frequently', label: 'Frequently' },
  ]},
};

// B2-4: worded as ASSERTIONS to be checked (the group header is "check all that
// apply"), not as questions. `value` keys are unchanged — scoring is keyed off
// them, so this is a display-only reword.
const Q9_OPTIONS: Opt[] = [
  { value: 'professional_experience', label: 'I have professional experience with financial assets' },
  { value: 'managed_own_assets', label: 'I have directly managed my own financial assets' },
  { value: 'used_brokerage', label: 'I have used brokerage services' },
  { value: 'used_advisory', label: 'I have used discretionary management or investment advisory services' },
  { value: 'degree_finance_econ', label: 'I have an academic degree in Finance or Economics' },
  { value: 'degree_math_stats', label: 'I have an academic degree in Mathematics or Statistics' },
  { value: 'knowledge_low', label: 'I consider my knowledge of financial matters to be low' },
];

// P17 (Pedro 2026-10-11): the knowledge & experience table — the MiFID list of financial
// instruments (MiFID II Annex I Section C, in plain words), each with a Knowledge and an
// Experience tick. Capture only; it does not feed the score. Keys = backend
// `aq_scoring.KE_INSTRUMENTS`. WORDING IS A DRAFT for Pedro's review (client-facing).
const KE_INSTRUMENTS: Opt[] = [
  { value: 'shares', label: 'Shares' },
  { value: 'bonds', label: 'Bonds and other debt securities' },
  { value: 'money_market', label: 'Money-market instruments (e.g. treasury bills, commercial paper)' },
  { value: 'funds', label: 'Investment funds (UCITS)' },
  { value: 'etfs', label: 'Exchange-traded funds (ETFs)' },
  { value: 'alternative_funds', label: 'Alternative investment funds (e.g. hedge, private-equity, real-estate funds)' },
  { value: 'structured_products', label: 'Structured products and certificates' },
  { value: 'structured_deposits', label: 'Structured deposits' },
  { value: 'derivatives', label: 'Derivatives (options, futures, swaps, forwards)' },
  { value: 'cfds', label: 'Contracts for difference (CFDs)' },
  { value: 'emission_allowances', label: 'Emission allowances' },
];
type KeTable = Record<string, { knowledge?: boolean; experience?: boolean }>;

const SUST_INTEREST_OPTIONS: Opt[] = [
  { value: 'none', label: 'No particular interest' },
  { value: 'no_minimum', label: 'Yes, but without a minimum share of the portfolio, and only in a way that does not sacrifice return/risk characteristics' },
  { value: 'min_10', label: 'Yes, in at least 10% of the portfolio, even with some sacrifice of return/risk characteristics' },
  { value: 'min_25', label: 'Yes, in at least 25% of the portfolio, even with some sacrifice of return/risk characteristics' },
  { value: 'min_50', label: 'Yes, in at least 50% of the portfolio, even with some sacrifice of return/risk characteristics' },
];

const SUST_EXIGENCY_OPTIONS: Opt[] = [
  { value: 'art8', label: 'My ESG preferences are satisfied by products that promote environmental or social characteristics (SFDR Art. 8)' },
  { value: 'art9', label: 'My ESG preferences are only satisfied by products with sustainable investment as their main objective (SFDR Art. 9)' },
];

const FIN_FIELDS: { key: string; label: string }[] = [
  { key: 'financial_assets', label: 'Financial assets' },
  { key: 'physical_assets', label: 'Physical assets' },
  { key: 'other_assets', label: 'Other assets' },
  { key: 'liabilities_st', label: 'Liabilities — short term' },
  { key: 'liabilities_lt', label: 'Liabilities — long term' },
  { key: 'total_available_financial_assets', label: 'Total Available Financial Assets' },
  { key: 'annual_income', label: 'Annual Income (net)' },
  { key: 'annual_expenses', label: 'Annual Expenses' },
  { key: 'estimated_investment', label: 'Estimated Investment Value' },
];

export const ESG_LABELS: Record<EsgScore, string> = {
  ESG_0: 'ESG_0',
  ESG_8_0_10: 'ESG_8 (0-10%)', ESG_8_10_25: 'ESG_8 (10-25%)',
  ESG_8_25_50: 'ESG_8 (25-50%)', ESG_8_50_100: 'ESG_8 (50-100%)',
  ESG_9_0_10: 'ESG_9 (0-10%)', ESG_9_10_25: 'ESG_9 (10-25%)',
  ESG_9_25_50: 'ESG_9 (25-50%)', ESG_9_50_100: 'ESG_9 (50-100%)',
};

const SECTION_LABELS: Record<string, string> = {
  investment_purpose: 'Investment Purpose',
  time_horizon: 'Time Horizon',
  risk_tolerance: 'Risk Tolerance',
  knowledge_experience: 'Knowledge & Experience',
};

// Allocation labels + the chart palette (visual continuity with the
// NAV/DD/VaR illustrations; Cash-family in lighter greys).
// (labels shared with the Agreements list SAA column — utils/allocation.ts)
const ALLOC_COLORS: Record<string, string> = {
  CASH: '#c4c9d0', CASH_PLUS: '#aab1ba', STB: '#8f97a3',
  SAA00: '#5b6470', SAA10: '#1d4e89', SAA20: '#2a6fb0', SAA30: '#0f6b6e',
  SAA40: '#2e8b57', SAA50: '#6aa84f', SAA60: '#c8a13a', SAA70: '#d97a29',
  SAA80: '#c0532f', SAA90: '#7c2230', SAA100: '#4a1a3d',
};

function optionLabel(qKey: string, value: unknown): string {
  if (qKey === 'q9_knowledge' || qKey === 'q1_purpose') {
    const opts = qKey === 'q9_knowledge' ? Q9_OPTIONS : Q.q1_purpose.options;
    const vals = (value as string[]) ?? [];
    return vals.map(v => opts.find(o => o.value === v)?.label ?? v).join('; ') || '—';
  }
  const table = qKey === 'sust_interest' ? SUST_INTEREST_OPTIONS
    : qKey === 'sust_exigency' ? SUST_EXIGENCY_OPTIONS
    : Q[qKey]?.options;
  return table?.find(o => o.value === value)?.label ?? (value ? String(value) : '—');
}

/** P17: the filed table in words — "Shares (knowledge, experience); Bonds (knowledge)".
 *  Older responses have no table → "not asked" (it was added 2026-10-11). */
function keSummary(value: unknown): string {
  if (value == null) return 'not asked in this version';
  const table = value as KeTable;
  const parts = KE_INSTRUMENTS.flatMap(o => {
    const r = table[o.value];
    const ticks = [r?.knowledge && 'knowledge', r?.experience && 'experience'].filter(Boolean);
    return ticks.length ? [`${o.label} (${ticks.join(', ')})`] : [];
  });
  return parts.join('; ') || 'none ticked';
}

function subtotals(fin: Record<string, unknown>) {
  const n = (k: string): number | null => {
    const v = fin[k];
    if (v === null || v === undefined || v === '') return null;
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
  };
  const fa = n('financial_assets'), pa = n('physical_assets'), oa = n('other_assets');
  const lst = n('liabilities_st'), llt = n('liabilities_lt');
  const inc = n('annual_income'), exp = n('annual_expenses');
  const gross = fa !== null && pa !== null && oa !== null ? fa + pa + oa : null;
  const liab = lst !== null && llt !== null ? lst + llt : null;
  return [
    { label: 'Total Net Assets', formula: 'Gross Assets − Total Liabilities',
      value: gross !== null && liab !== null ? gross - liab : null },
    { label: 'Total Net Assets ST', formula: 'Gross Assets − ST Liabilities',
      value: gross !== null && lst !== null ? gross - lst : null },
    { label: 'Net Financial Assets ST', formula: 'Financial Assets − ST Liabilities',
      value: fa !== null && lst !== null ? fa - lst : null },
    { label: 'Net Financial Assets', formula: 'Financial Assets − Liabilities',
      value: fa !== null && liab !== null ? fa - liab : null },
    { label: 'Annual Savings', formula: 'Annual Income − Annual Expenses',
      value: inc !== null && exp !== null ? inc - exp : null },
  ];
}

// ─── Allocation pill ───────────────────────────────────────────────────────

function AllocationPill({
  value, active, clickable, onPick,
}: { value: string; active: boolean; clickable: boolean; onPick?: () => void }) {
  const color = ALLOC_COLORS[value] ?? '#5b6470';
  return (
    <Badge
      variant={active ? 'filled' : 'outline'}
      styles={{
        root: {
          backgroundColor: active ? color : undefined,
          borderColor: color,
          color: active ? '#fff' : color,
          cursor: clickable ? 'pointer' : 'default',
          textTransform: 'none',
        },
      }}
      onClick={clickable && onPick ? onPick : undefined}
    >
      {allocLabel(value)}
    </Badge>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────

export function AqSection({
  agreementId, editMode, draft, onDraftChange, onClearScores,
  currentAllocation, onPickAllocation, pendingDeletes, onToggleDelete,
}: {
  agreementId: UUID;
  editMode: boolean;
  draft: AqDraft | null;
  onDraftChange: (d: AqDraft | null) => void;
  onClearScores: () => void;
  /** The Strategic Allocation currently staged on the page form. */
  currentAllocation: string;
  onPickAllocation: (a: StrategicAllocation) => void;
  /** Assessment versions staged for deletion (commit on Save). */
  pendingDeletes: UUID[];
  onToggleDelete: (id: UUID) => void;
}) {
  const historyQ = useAqResponses(agreementId);
  const preview = useAqPreview(agreementId);
  const conversionQ = useSuitabilityConversion();
  const { confirm } = useAlertModal();
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Round-3 final: the correspondence modal opens ONLY via the explicit
  // link (Pedro 2026-06-12 — auto-pop on completion removed after smoke).
  const [viewing, setViewing] = useState<AqResponse | null>(null);
  // Round-3 polish: when a pick exceeds, the warning renders INSIDE the
  // modal (below the table) when it's open; toast only otherwise.
  const [pickWarning, setPickWarning] = useState<string | null>(null);
  // SAA chart modals — one of three images; null = closed.
  const [chartModal, setChartModal] = useState<SaaSection | null>(null);
  // SAA matrix table shows only after an SAA is actively picked this session
  // (not auto-shown for the agreement's pre-existing choice). #11 Inc 7 polish.
  const [pickedAlloc, setPickedAlloc] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  useEffect(() => {
    if (!draft || !editMode) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      // The staged SAA rides along so the checks include its drawdown against Q8 (P17).
      preview.mutate({
        answers: draft.answers, financials: draft.financials,
        allocation_chosen: (currentAllocation || null) as StrategicAllocation | null,
      });
    }, 600);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, editMode, currentAllocation]);

  const responses = historyQ.data?.items ?? [];
  const latest: AqResponse | null = responses[0] ?? null;
  const p: AqPreview | null = preview.data ?? null;
  const score = p?.profile_score ?? null;
  const conversionRows: SuitabilityConversionRow[] = conversionQ.data?.items ?? [];
  const matchRow = matchConversionRow(conversionRows, score);

  function setAnswer(key: string, value: unknown) {
    if (!draft) return;
    onDraftChange({ ...draft, answers: { ...draft.answers, [key]: value } });
  }
  // P17: one tick in the knowledge & experience table.
  function setKe(instrument: string, field: 'knowledge' | 'experience', checked: boolean) {
    if (!draft) return;
    const table = (draft.answers.ke_table as KeTable | undefined) ?? {};
    setAnswer('ke_table', { ...table, [instrument]: { ...(table[instrument] ?? {}), [field]: checked } });
  }
  function setFinancial(key: string, value: number | string) {
    if (!draft) return;
    onDraftChange({
      ...draft,
      financials: { ...draft.financials, [key]: value === '' ? null : value },
    });
  }

  function pickAllocation(a: string) {
    onPickAllocation(a as StrategicAllocation);
    setPickedAlloc(a);  // reveal the chosen SAA's allocation table
    // The correspondence table is inline now, so the "exceeds the suitability
    // ceiling" warning always renders inline below it (no toast). #11 Inc 7 UX.
    if (allocationExceeds(conversionRows, score, a) === true) {
      setPickWarning(`${allocLabel(a)} ranks above the correspondence for profile ${score}. You can still save — the inconsistency is recorded and flagged to Compliance.`);
    } else {
      setPickWarning(null);  // a compliant pick clears the inline warning
    }
  }

  async function handleClearQuestionnaire() {
    const ok = await confirm({
      title: 'Clear the questionnaire?',
      message: 'This clears the staged questionnaire answers AND the Suitability Score and ESG Score fields. Saved assessments on record are not affected.',
      confirmLabel: 'Proceed',
      cancelLabel: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    onDraftChange({ ...EMPTY_AQ_DRAFT });
    onClearScores();
  }

  const interest = draft?.answers?.sust_interest as string | undefined;
  const subs = draft ? subtotals(draft.financials) : [];

  const correspondenceTable = (
    <Table withTableBorder>
      <Table.Thead><Table.Tr>
        <Table.Th>From</Table.Th><Table.Th>To</Table.Th><Table.Th>Allocations</Table.Th>
      </Table.Tr></Table.Thead>
      <Table.Tbody>
        {conversionRows.map(r => (
          <Table.Tr key={r.id}
            bg={matchRow?.id === r.id ? 'var(--mantine-color-azul-0)' : undefined}>
            <Table.Td>{r.score_from}</Table.Td>
            <Table.Td>{r.score_to}</Table.Td>
            <Table.Td>
              <Group gap={6}>
                {r.allocations.map(a => (
                  <AllocationPill key={a} value={a}
                    active={currentAllocation === a}
                    clickable={editMode}
                    onPick={() => pickAllocation(a)} />
                ))}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );

  return (
    <Card withBorder p="lg" radius="md" mt="md">
      <Stack>
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <IconClipboardCheck size={20} />
            <Title order={4}>Adequacy questionnaire</Title>
          </Group>
          {editMode && !draft && (
            <Group gap="xs">
              <Button size="xs" leftSection={<IconCalculator size={14} />}
                onClick={() => onDraftChange({ ...EMPTY_AQ_DRAFT })}>
                New assessment
              </Button>
              <FileButton accept="application/pdf" onChange={async (file) => {
                if (!file) return;
                setUploadBusy(true);
                try {
                  const r = await importAqPdf(agreementId, file);
                  onDraftChange({ ...EMPTY_AQ_DRAFT, answers: r.answers, financials: r.financials });
                  notifications.show({
                    color: r.warnings.length ? 'orange' : 'green',
                    title: 'Questionnaire uploaded',
                    message: r.warnings.length
                      ? r.warnings.join(' ')
                      : 'Loaded from PDF — review the answers and Save.',
                  });
                } catch (e) {
                  notifications.show({
                    color: 'red', title: 'Upload failed',
                    message: e instanceof Error ? e.message : 'Could not read the questionnaire PDF.',
                  });
                } finally {
                  setUploadBusy(false);
                }
              }}>
                {(props) => (
                  <Button {...props} size="xs" variant="default" loading={uploadBusy}
                    leftSection={<IconUpload size={14} />}>
                    Upload questionnaire
                  </Button>
                )}
              </FileButton>
            </Group>
          )}
          {editMode && draft && (
            <Stack gap={6} align="flex-end">
              <Button size="xs" variant="default" leftSection={<IconX size={14} />}
                onClick={() => onDraftChange(null)}>
                Unstage assessment
              </Button>
              <Button size="xs" variant="default" color="red" leftSection={<IconEraser size={14} />}
                onClick={handleClearQuestionnaire}>
                Clear questionnaire
              </Button>
            </Stack>
          )}
        </Group>

        {!latest && (
          <Text size="sm" c="dimmed">
            No assessment on record yet{editMode ? ' — use "New assessment" to start one' : ' (enter Edit mode to start one)'}.
          </Text>
        )}

        {draft && (
          <Stack gap="lg">
            <Divider label="I — Investment Objectives" labelPosition="left" />
            <Checkbox.Group label={Q.q1_purpose.label} value={(draft.answers.q1_purpose as string[]) ?? []}
              onChange={(v) => setAnswer('q1_purpose', v)}>
              <Stack gap={6} mt={6}>{Q.q1_purpose.options.map(o =>
                <Checkbox key={o.value} value={o.value} label={o.label} />)}</Stack>
            </Checkbox.Group>
            <Radio.Group label={Q.q2_horizon.label} value={(draft.answers.q2_horizon as string) ?? ''}
              onChange={(v) => setAnswer('q2_horizon', v)}>
              <Stack gap={6} mt={6}>{Q.q2_horizon.options.map(o =>
                <Radio key={o.value} value={o.value} label={o.label} />)}</Stack>
            </Radio.Group>

            <Divider label="II — Financial Capacity" labelPosition="left" />
            <Text size="xs" c="dimmed">All monetary values in EUR '000 (thousands).</Text>
            <Grid>
              {FIN_FIELDS.map(f => (
                <Grid.Col span={4} key={f.key}>
                  <NumberInput
                    label={f.label} thousandSeparator=" "
                    value={(draft.financials[f.key] as number | undefined) ?? ''}
                    onChange={(v) => setFinancial(f.key, v as number | string)}
                  />
                </Grid.Col>
              ))}
            </Grid>
            <Table withTableBorder w="auto">
              <Table.Tbody>
                {subs.map(s => (
                  <Table.Tr key={s.label}>
                    <Table.Td><Text size="sm" fw={500}>{s.label}</Text>
                      <Text size="xs" c="dimmed">{s.formula}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'right', minWidth: 110 }}>
                      <Text size="sm" fw={600} c={s.value !== null && s.value < 0 ? 'bordeaux.7' : undefined}>
                        {s.value !== null ? s.value.toLocaleString() : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Radio.Group label={Q.q4_asset_change.label} value={(draft.answers.q4_asset_change as string) ?? ''}
              onChange={(v) => setAnswer('q4_asset_change', v)}>
              <Group gap="lg" mt={6}>{Q.q4_asset_change.options.map(o =>
                <Radio key={o.value} value={o.value} label={o.label} />)}</Group>
            </Radio.Group>
            <Radio.Group label={Q.q5_income_change.label} value={(draft.answers.q5_income_change as string) ?? ''}
              onChange={(v) => setAnswer('q5_income_change', v)}>
              <Group gap="lg" mt={6}>{Q.q5_income_change.options.map(o =>
                <Radio key={o.value} value={o.value} label={o.label} />)}</Group>
            </Radio.Group>

            <Divider label="III — Risk Tolerance" labelPosition="left" />
            <Radio.Group label={Q.q7_risk_return.label} value={(draft.answers.q7_risk_return as string) ?? ''}
              onChange={(v) => setAnswer('q7_risk_return', v)}>
              <Stack gap={6} mt={6}>{Q.q7_risk_return.options.map(o =>
                <Radio key={o.value} value={o.value} label={o.label} />)}</Stack>
            </Radio.Group>
            {/* q8 + SAA chart buttons — split into two columns so the chart
                buttons sit in the space to the right of the radio options. */}
            <Grid gutter="xl" align="flex-start">
              <Grid.Col span={9}>
                <Radio.Group label={Q.q8_devaluation.label} value={(draft.answers.q8_devaluation as string) ?? ''}
                  onChange={(v) => setAnswer('q8_devaluation', v)}>
                  <Stack gap={6} mt={6}>{Q.q8_devaluation.options.map(o =>
                    <Radio key={o.value} value={o.value} label={o.label} />)}</Stack>
                </Radio.Group>
              </Grid.Col>
              <Grid.Col span={3}>
                <Stack gap={8} pt={28}>
                  <Button size="xs" variant="default" fullWidth
                    onClick={() => setChartModal('stats')}>
                    Show statistics
                  </Button>
                  <Button size="xs" variant="default" fullWidth
                    onClick={() => setChartModal('cumulative')}>
                    Show growth paths
                  </Button>
                  <Button size="xs" variant="default" fullWidth
                    onClick={() => setChartModal('drawdown')}>
                    Show drawdowns
                  </Button>
                  <Button size="xs" variant="default" fullWidth
                    onClick={() => setChartModal('ddScatter')}>
                    Show risk / return
                  </Button>
                  <Button size="xs" variant="default" fullWidth
                    onClick={() => setChartModal('q8fit')}>
                    Show fit with Q8
                  </Button>
                </Stack>
              </Grid.Col>
            </Grid>

            <Divider label="IV — Knowledge and Experience" labelPosition="left" />
            <Checkbox.Group label="Please check all that apply"
              value={(draft.answers.q9_knowledge as string[]) ?? []}
              onChange={(v) => setAnswer('q9_knowledge', v)}>
              <Stack gap={6} mt={6}>{Q9_OPTIONS.map(o =>
                <Checkbox key={o.value} value={o.value} label={o.label} />)}</Stack>
            </Checkbox.Group>
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                For each type of financial instrument, tick Knowledge if you know what it is, and
                Experience if you have invested in it.
              </Text>
              <Table withTableBorder verticalSpacing={4} horizontalSpacing="sm" maw={720}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Financial instrument</Table.Th>
                    <Table.Th ta="center" w={110}>Knowledge</Table.Th>
                    <Table.Th ta="center" w={110}>Experience</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {KE_INSTRUMENTS.map(o => {
                    const row = ((draft.answers.ke_table as KeTable | undefined) ?? {})[o.value] ?? {};
                    return (
                      <Table.Tr key={o.value}>
                        <Table.Td><Text size="sm">{o.label}</Text></Table.Td>
                        {(['knowledge', 'experience'] as const).map(f => (
                          <Table.Td key={f} ta="center">
                            <Checkbox mx="auto" w="fit-content" checked={!!row[f]}
                              aria-label={`${o.label} — ${f === 'knowledge' ? 'Knowledge' : 'Experience'}`}
                              onChange={(e) => setKe(o.value, f, e.currentTarget.checked)} />
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Stack>
            {(['q10_experience', 'q11_last_trade', 'q12_news'] as const).map(key => (
              <Radio.Group key={key} label={Q[key].label} value={(draft.answers[key] as string) ?? ''}
                onChange={(v) => setAnswer(key, v)}>
                <Stack gap={6} mt={6}>{Q[key].options.map(o =>
                  <Radio key={o.value} value={o.value} label={o.label} />)}</Stack>
              </Radio.Group>
            ))}

            <Divider label="V — ESG" labelPosition="left" />
            <Radio.Group
              label="Considering the legal and complementary information, are you interested in financial instruments that take sustainability criteria into account?"
              value={(draft.answers.sust_interest as string) ?? ''}
              onChange={(v) => {
                if (!draft) return;
                onDraftChange({
                  ...draft,
                  answers: {
                    ...draft.answers, sust_interest: v,
                    ...(v === 'none' ? { sust_exigency: null } : {}),
                  },
                });
              }}>
              <Stack gap={6} mt={6}>{SUST_INTEREST_OPTIONS.map(o =>
                <Radio key={o.value} value={o.value} label={o.label} />)}</Stack>
            </Radio.Group>
            {interest && interest !== 'none' && (
              <Radio.Group label="Which products satisfy your ESG preferences?"
                value={(draft.answers.sust_exigency as string) ?? ''}
                onChange={(v) => setAnswer('sust_exigency', v)}>
                <Stack gap={6} mt={6}>{SUST_EXIGENCY_OPTIONS.map(o =>
                  <Radio key={o.value} value={o.value} label={o.label} />)}</Stack>
              </Radio.Group>
            )}

            <Textarea label="Notes" value={draft.notes}
              onChange={(e) => onDraftChange({ ...draft, notes: e.currentTarget.value })} />

            {p && (
              p.complete ? (
                <Card withBorder p="md" radius="md" bg="var(--mantine-color-gray-0)">
                  <Stack gap="xs">
                    <Group gap="lg">
                      {Object.entries(p.section_scores).map(([k, v]) => (
                        <Text size="sm" key={k}>
                          {SECTION_LABELS[k] ?? k}: <strong>{v}</strong>
                        </Text>
                      ))}
                    </Group>
                    <Group gap="md" align="center">
                      <Badge size="lg" variant="filled" color={p.profile_score != null && p.profile_score < 0 ? 'red' : 'azul'}>
                        Suitability Score: {p.profile_score}
                      </Badge>
                      <Badge size="lg" variant="light" color="petroleo">
                        ESG: {p.esg_derived ? ESG_LABELS[p.esg_derived] : '—'}
                      </Badge>
                    </Group>

                    {/* Correspondence table, inline (#11 Inc 7 UX, Pedro 2026-06-19):
                        picking the Strategic Allocation happens HERE. Matched row
                        (for this score) highlighted; pick a pill to choose the SAA. */}
                    <Stack gap={6}>
                      <Text size="sm" fw={600}>Suitability Score → Strategic Allocation — choose the allocation:</Text>
                      {score !== null && !matchRow && (
                        <Text size="xs" c="dimmed">No matching range in the correspondence table for this score.</Text>
                      )}
                      {correspondenceTable}
                      {pickWarning && (
                        <Alert color="orange" icon={<IconAlertCircle size={16} />} title="Allocation exceeds Suitability Score">
                          <Text size="sm">{pickWarning}</Text>
                        </Alert>
                      )}
                      <Text size="xs" c="dimmed">
                        The Strategic Allocation is your pick (independent of the score). More
                        conservative picks are always permissible; picking above the matched row
                        is recorded and flagged to Compliance.
                      </Text>
                    </Stack>

                    {pickedAlloc && (
                      <Stack gap={4}>
                        <Text size="xs" c="dimmed">
                          Allocation table for the SAA you selected (the adviser's choice — not derived from the questionnaire):
                        </Text>
                        <SaaMatrixView allocationCode={pickedAlloc} />
                      </Stack>
                    )}
                    {p.checks.length > 0 && (
                      <Alert color="orange" icon={<IconAlertCircle size={16} />} title="Advisory checks">
                        <Stack gap={2}>
                          {p.checks.map((c, i) => <Text size="sm" key={i}>{c}</Text>)}
                        </Stack>
                      </Alert>
                    )}
                    <Text size="xs" c="dimmed">
                      The assessment is staged — it is recorded when you press Save Changes.
                    </Text>
                  </Stack>
                </Card>
              ) : (
                <Text size="sm" c="dimmed">
                  {p.missing.length} item{p.missing.length === 1 ? '' : 's'} left to answer before the profile computes.
                </Text>
              )
            )}
          </Stack>
        )}

        {/* History — the working object (always shown when records exist). */}
        {responses.length > 0 && (
          <Table withTableBorder highlightOnHover>
            <Table.Thead><Table.Tr>
              <Table.Th>Suitability score</Table.Th>
              <Table.Th>ESG score</Table.Th>
              <Table.Th>Allocation chosen</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Advisory notes</Table.Th>
              <Table.Th style={{ width: 90 }} />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {responses.map(r => {
                const stagedDelete = pendingDeletes.includes(r.id);
                return (
                  <Table.Tr key={r.id}
                    bg={stagedDelete ? 'var(--mantine-color-orange-0)' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setViewing(r)}>
                    <Table.Td>{r.profile_score}</Table.Td>
                    <Table.Td>{r.esg_derived ? ESG_LABELS[r.esg_derived] : '—'}</Table.Td>
                    <Table.Td>
                      {r.allocation_chosen
                        ? <Group gap={6}>
                            <AllocationPill value={r.allocation_chosen} active clickable={false} />
                            {r.allocation_exceeds && (
                              <Tooltip label="At the time of this assessment, the chosen allocation exceeded the Suitability Score correspondence." withArrow>
                                <IconAlertCircle size={15} color="var(--mantine-color-orange-7)" />
                              </Tooltip>
                            )}
                          </Group>
                        : '—'}
                    </Table.Td>
                    <Table.Td>{new Date(r.created_at).toLocaleDateString()}</Table.Td>
                    <Table.Td>
                      {stagedDelete
                        ? <Badge color="orange" variant="light">Pending delete</Badge>
                        : (r.checks.length || '—')}
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label={editMode ? 'Load this version into the form (saving files a NEW version)' : 'Enter Edit mode to edit'} withArrow>
                          <span>
                            <Button size="compact-xs" variant="subtle" disabled={!editMode || stagedDelete}
                              onClick={() => onDraftChange({
                                answers: { ...(r.answers as Record<string, unknown>) },
                                financials: { ...(r.financials as Record<string, unknown>) },
                                notes: r.notes ?? '',
                              })}>
                              <IconPencil size={15} />
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip label={editMode
                          ? (stagedDelete ? 'Unstage this delete' : 'Stage delete (commits on Save Changes)')
                          : 'Enter Edit mode to delete'} withArrow>
                          <span>
                            <Button size="compact-xs" variant="subtle" color={stagedDelete ? 'gray' : 'red'}
                              disabled={!editMode}
                              onClick={() => onToggleDelete(r.id)}>
                              {stagedDelete ? <IconArrowBackUp size={15} /> : <IconTrash size={15} />}
                            </Button>
                          </span>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {/* SAA live analyses (all SAAs, full history) — full-page; replaces the old static
          images (#4 Inc 3). One element per button: stats / cumulative / drawdown / risk-DD. */}
      <SaaAnalysisModal section={chartModal} onClose={() => setChartModal(null)} />

      {/* Read-only viewer for a recorded version. */}
      <Modal opened={viewing !== null} onClose={() => setViewing(null)} centered size="lg"
        title={<Text fw={600}>
          Assessment of {viewing ? new Date(viewing.created_at).toLocaleDateString() : ''} (read-only)
        </Text>}>
        {viewing && (
          <Stack gap="sm">
            <Group gap="md">
              <Badge size="lg" variant="filled" color={(viewing.profile_score ?? 0) < 0 ? 'red' : 'azul'}>
                Suitability Score: {viewing.profile_score}
              </Badge>
              <Badge size="lg" variant="light" color="petroleo">
                ESG: {viewing.esg_derived ? ESG_LABELS[viewing.esg_derived] : '—'}
              </Badge>
              {viewing.allocation_chosen && (
                <Group gap={6}>
                  <Text size="sm">Allocation chosen:</Text>
                  <AllocationPill value={viewing.allocation_chosen} active clickable={false} />
                  {viewing.allocation_exceeds && (
                    <Text size="xs" c="orange.7">exceeded the correspondence at the time</Text>
                  )}
                </Group>
              )}
            </Group>
            <Divider label="Answers" labelPosition="left" />
            {(['q1_purpose', 'q2_horizon'] as const).map(k => (
              <Text size="sm" key={k}><strong>{Q[k].label}:</strong> {optionLabel(k, viewing.answers[k])}</Text>
            ))}
            <Text size="sm"><strong>Financials:</strong>{' '}
              {FIN_FIELDS.map(f => `${f.label}: ${Number(viewing.financials[f.key] ?? 0).toLocaleString()}`).join(' · ')}
            </Text>
            <Stack gap={2}>
              {subtotals(viewing.financials).map(s => (
                <Text size="xs" c="dimmed" key={s.label}>
                  {s.label}: {s.value !== null ? s.value.toLocaleString() : '—'}
                </Text>
              ))}
            </Stack>
            {(['q4_asset_change', 'q5_income_change', 'q7_risk_return', 'q8_devaluation'] as const).map(k => (
              <Text size="sm" key={k}><strong>{Q[k].label}:</strong> {optionLabel(k, viewing.answers[k])}</Text>
            ))}
            <Text size="sm"><strong>Knowledge & experience (check-all):</strong> {optionLabel('q9_knowledge', viewing.answers.q9_knowledge)}</Text>
            <Text size="sm"><strong>Knowledge & experience by instrument:</strong> {keSummary(viewing.answers.ke_table)}</Text>
            {(['q10_experience', 'q11_last_trade', 'q12_news'] as const).map(k => (
              <Text size="sm" key={k}><strong>{Q[k].label}:</strong> {optionLabel(k, viewing.answers[k])}</Text>
            ))}
            <Text size="sm"><strong>ESG interest:</strong> {optionLabel('sust_interest', viewing.answers.sust_interest)}</Text>
            {viewing.answers.sust_exigency ? (
              <Text size="sm"><strong>ESG exigency:</strong> {optionLabel('sust_exigency', viewing.answers.sust_exigency)}</Text>
            ) : null}
            {viewing.notes && <Text size="sm"><strong>Notes:</strong> {viewing.notes}</Text>}
            {viewing.checks.length > 0 && (
              <Alert color="orange" icon={<IconAlertCircle size={16} />} title="Advisory checks (as recorded)">
                <Stack gap={2}>{viewing.checks.map((c, i) => <Text size="sm" key={i}>{c}</Text>)}</Stack>
              </Alert>
            )}
            <Group gap="lg">
              {Object.entries(viewing.section_scores).map(([k, v]) => (
                <Text size="sm" key={k}>{SECTION_LABELS[k] ?? k}: <strong>{String(v)}</strong></Text>
              ))}
            </Group>
          </Stack>
        )}
      </Modal>
    </Card>
  );
}
